/**
 * Module: Usage Tracking
 * SpecReferenceID: FRONTEND-WEB-V1
 * Requirements: FRONTEND-WEB-V1-R10, SEARCH-HYBRID-V1-R09
 *
 * Shared utility for tracking usage actions across search and chat pages.
 * FRONTEND-WEB-V1-R10: COUNTER-compliant usage tracking.
 * SEARCH-HYBRID-V1-R09: Capture usage metrics including link-open and refinements.
 */

import api from '@/lib/api';

export type UsageActionType =
  | 'link_open'
  | 'query_refinement'
  | 'result_expand'
  | 'copy_citation'
  | 'chat_query'
  | 'search_query'
  | 'citation_expand';

export interface UsageActionParams {
  document_id?: string;
  query_text?: string;
  previous_query?: string;
  ranking_position?: number;
  mode?: string;
  /** LOGGING-V1-R07: Search mode that triggered this access (chat, semantic, simple, galen) */
  referrer_mode?: string;
  /** LOGGING-V1-R07: Guideline section ID for section-level resource identification */
  section_id?: number | null;
  /** FRONTEND-WEB-V1-R10: Human-readable guideline/publication name */
  guideline_name?: string;
  /** FRONTEND-WEB-V1-R10: External URL to the guideline source */
  external_guideline_url?: string;
  /** FRONTEND-WEB-V1-R10: Section name for section-level opens */
  section_name?: string;
  /** LOGGING-V1-R07: Guideline identifier for COUNTER resource identification */
  guideline_id?: string;
}

/**
 * Track a usage action via the API.
 * FRONTEND-WEB-V1-R10: COUNTER-compliant usage tracking.
 * Silently fails so tracking never interrupts user experience.
 */
export async function trackUsageAction(
  actionType: UsageActionType,
  params: UsageActionParams
): Promise<void> {
  try {
    await api.post('/search/usage', {
      action_type: actionType,
      ...params,
    });
  } catch {
    // Silently fail - usage tracking should not interrupt UX
    console.debug('Usage tracking failed:', actionType);
  }
}
